import {
  AdHocInvoiceLineItemSchema,
  RegistryLinkedInvoiceLineItemSchema,
} from '@trellis/shared'
import { useCallback, useState } from 'react'

import type { SortableLineItem } from '@/components/document-line-items-section'

/** Field-level error messages for a single line item row, keyed by field name. */
export type LineItemRowErrors = Record<string, string>

/** Field-level error messages for every invalid row, keyed by row `_id`. */
export type LineItemErrors = Record<string, LineItemRowErrors>

function validateLineItem(lineItem: SortableLineItem): LineItemRowErrors {
  const result =
    lineItem.type === 'registry'
      ? RegistryLinkedInvoiceLineItemSchema.safeParse({
          lineItemId: lineItem.lineItemId,
          quantity: lineItem.quantity,
          discount: isNaN(lineItem.discount) ? undefined : lineItem.discount,
          discountType: lineItem.discountType,
        })
      : AdHocInvoiceLineItemSchema.safeParse({
          title: lineItem.title,
          description: lineItem.description ?? undefined,
          unitPrice: lineItem.unitPrice,
          quantity: lineItem.quantity,
          unitType: lineItem.unitType,
          discount: isNaN(lineItem.discount) ? undefined : lineItem.discount,
          discountType: lineItem.discountType,
          taxable: lineItem.taxable,
        })

  if (result.success) return {}

  const rowErrors: LineItemRowErrors = {}
  for (const issue of result.error.issues) {
    const field = String(issue.path[0])
    if (!rowErrors[field]) rowErrors[field] = issue.message
  }
  return rowErrors
}

/**
 * Validates ad-hoc/registry line item rows against the same Zod schemas the backend
 * enforces, so a blank or invalid row is caught — and shown inline — before a save
 * request is ever sent.
 */
export function useLineItemValidation() {
  const [errorsByRow, setErrorsByRow] = useState<LineItemErrors>({})

  const validate = useCallback((lineItems: SortableLineItem[]): boolean => {
    const nextErrors: LineItemErrors = {}
    for (const lineItem of lineItems) {
      const rowErrors = validateLineItem(lineItem)
      if (Object.keys(rowErrors).length > 0) nextErrors[lineItem._id] = rowErrors
    }
    setErrorsByRow(nextErrors)
    return Object.keys(nextErrors).length === 0
  }, [])

  return { errorsByRow, validate }
}
